import { Controller } from "@hotwired/stimulus"
import { place } from "../lib/anchor"

// A tooltip for an icon-only control, or a one-line clarification of one.
//
// These were `title` attributes, which a browser shows to a mouse after a
// pause and to nobody else: a keyboard user tabbing onto the theme toggle, or
// a thumb on a phone, got the icon and nothing more. DESIGN_SYSTEM §2.4 asks
// for "aria-label and a tooltip" on every icon-only button, and a title is
// only half of that.
//
// The bubble is created here rather than written into each template, so the
// markup is one attribute pair — data-controller and the text — and a Vue
// island can carry it exactly as an ERB view does. Stimulus watches the whole
// document, so a trigger Vue renders connects like any other.
//
// The bubble is a manual popover: that puts it in the top layer, where it
// draws over the help sheet instead of under it, and it never clips against
// a scrolling table. Where the browser has no popover support the bubble is
// simply shown in place — fixed-positioned, so the result is the same.
//
// Says nothing the control does not already say. The text is the control's
// name; aria-describedby is only added where the label is something else, so
// a screen reader is not told "Sign out, button, Sign out".
let sequence = 0

export default class extends Controller {
  static values = { text: String }

  connect() {
    this.bubble = document.createElement("div")
    this.bubble.className = "tooltip"
    this.bubble.id = `tooltip-${++sequence}`
    this.bubble.setAttribute("role", "tooltip")
    this.bubble.setAttribute("popover", "manual")
    this.bubble.textContent = this.textValue
    document.body.appendChild(this.bubble)

    if (this.element.getAttribute("aria-label") !== this.textValue) {
      this.element.setAttribute("aria-describedby", this.bubble.id)
    }

    this.show = this.show.bind(this)
    this.hide = this.hide.bind(this)
    this.escape = this.escape.bind(this)
    this.element.addEventListener("mouseenter", this.show)
    this.element.addEventListener("mouseleave", this.hide)
    this.element.addEventListener("focus", this.show)
    this.element.addEventListener("blur", this.hide)
  }

  // The text is written once at connect, so a trigger whose
  // data-tooltip-text-value is bound to changing state would otherwise keep
  // the bubble it was born with. Stimulus calls this on every change to the
  // attribute, including once before connect(), when there is no bubble yet.
  textValueChanged() {
    if (this.bubble) this.bubble.textContent = this.textValue
  }

  disconnect() {
    this.hide()
    this.element.removeEventListener("mouseenter", this.show)
    this.element.removeEventListener("mouseleave", this.hide)
    this.element.removeEventListener("focus", this.show)
    this.element.removeEventListener("blur", this.hide)
    this.bubble.remove()
  }

  show() {
    if (typeof this.bubble.showPopover === "function") this.bubble.showPopover()
    else this.bubble.dataset.open = ""
    place(this.bubble, this.element, { side: "top", align: "center" })
    // Esc dismisses it wherever focus is: a pointer hovering the trigger has
    // not moved focus anywhere, so the trigger's own keydown would never see it.
    document.addEventListener("keydown", this.escape)
  }

  hide() {
    if (typeof this.bubble.hidePopover === "function") this.bubble.hidePopover()
    else delete this.bubble.dataset.open
    document.removeEventListener("keydown", this.escape)
  }

  escape(event) {
    if (event.key === "Escape") this.hide()
  }
}
